// Modal component for displaying registration funnel analysis
// Shows comprehensive funnel data with step-by-step breakdown and visualizations

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  VStack,
  HStack,
  Box,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Spinner,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Badge,
  useColorModeValue,
  SimpleGrid,
  Flex,
  Icon,
  Input,
  FormControl,
  FormLabel,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Select,
} from '@chakra-ui/react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid } from 'recharts';
import { FiTrendingDown, FiTrendingUp, FiUsers, FiTarget } from 'react-icons/fi';
import AnalyticsApi from '../../../../api/analytics';
import CustomFunnelChart from './CustomFunnelChart';
import { getCompanies } from '../../../../functions/companiesCache';

const RegistrationFunnelModal = ({ isOpen, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30); // Default to 30 days ago
    return date.toISOString().split('T')[0];
  });
  const [endDate, setEndDate] = useState(() => {
    const date = new Date();
    return date.toISOString().split('T')[0];
  });
  const [selectedCompanyId, setSelectedCompanyId] = useState('');
  const [companiesCache, setCompaniesCache] = useState({});

  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const chartBgColor = useColorModeValue('gray.50', 'gray.700');
  const chartTextColor = useColorModeValue('gray.800', 'white');

  useEffect(() => {
    if (isOpen) {
      loadCompanies();
      fetchFunnelReport();
    }
  }, [isOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadCompanies = async () => {
    try {
      const companies = await getCompanies();
      setCompaniesCache(companies);
    } catch (err) {
      console.error('Error loading companies:', err);
    }
  };

  const fetchFunnelReport = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await AnalyticsApi.getRegistrationFunnelReport(
        startDate, 
        endDate, 
        selectedCompanyId || null
      );
      if (response.data) {
        setReportData(response.data);
      }
    } catch (err) {
      console.error('Error fetching registration funnel report:', err);
      setError('שגיאה בטעינת נתוני הדוח');
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = () => {
    fetchFunnelReport();
  };

  const handleCompanyChange = (event) => {
    setSelectedCompanyId(event.target.value);
  };

  const formatNumber = (num) => {
    return new Intl.NumberFormat('he-IL').format(num);
  };

  const formatPercentage = (num) => {
    return `${num.toFixed(1)}%`;
  };

  const prepareFunnelData = (funnelSteps) => {
    return (funnelSteps || []).map((step, index) => ({
      name: step.stepName,
      value: step.totalEvents,
      fill: getStepColor(index),
      stepNumber: step.stepNumber,
      conversionRate: step.conversionRate,
      dropOffRate: step.dropOffRate
    }));
  };

  const getStepColor = (index) => {
    const colors = ['#3182CE', '#38A169', '#D69E2E', '#E53E3E', '#805AD5', '#DD6B20'];
    return colors[index] || '#718096';
  };

  const CustomParameterTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0];
      return (
        <Box
          bg={bgColor}
          p={3}
          border="1px"
          borderColor="gray.200"
          borderRadius="md"
          boxShadow="lg"
        >
          <Text fontWeight="bold">{data.name}</Text>
          <Text>כמות: {formatNumber(data.value)}</Text>
          <Text>אחוז: {data.payload.percentage}%</Text>
        </Box>
      );
    }
    return null;
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent maxW="90vw" maxH="90vh">
        <ModalHeader
          borderBottom="1px"
          borderColor="gray.200"
          textAlign="center"
          fontSize="2xl"
          fontWeight="bold"
          color="blue.600"
          py={6}
        >
          <Flex align="center" justify="center">
            <Icon as={FiUsers} ml={3} />
            <Text>דוח Registration Funnel</Text>
          </Flex>
        </ModalHeader>
        <ModalCloseButton left={4} />

        <ModalBody p={6}>
          {/* Filters Section */}
          <Box mb={6} p={4} border="1px" borderColor={borderColor} borderRadius="lg" bg={bgColor}>
            <Text fontSize="lg" fontWeight="semibold" mb={4} textAlign="center">
              סינון נתונים
            </Text>
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4}>
              <FormControl>
                <FormLabel>תאריך התחלה</FormLabel>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>תאריך סיום</FormLabel>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                />
              </FormControl>
              <FormControl>
                <FormLabel>חברה</FormLabel>
                <Select
                  value={selectedCompanyId}
                  onChange={handleCompanyChange}
                  placeholder="כל החברות"
                >
                  {Object.entries(companiesCache).map(([id, name]) => (
                    <option key={id} value={id}>
                      {name}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
            <Flex justify="center" mt={4}>
              <Button colorScheme="blue" onClick={handleDateChange} isLoading={loading}>
                עדכן דוח
              </Button>
            </Flex>
          </Box>

          {loading ? (
            <VStack spacing={4} py={8}>
              <Spinner size="lg" />
              <Text>Loading Registration Funnel Report...</Text>
            </VStack>
          ) : error ? (
            <Alert status="error">
              <AlertIcon />
              <AlertTitle>שגיאה!</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : reportData ? (
            <VStack spacing={6} align="stretch">
              {/* Summary Stats */}
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>התחלות הרשמה</Text>
                      <Icon as={FiUsers} ml={3} color="blue.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="blue.500" textAlign="center" fontSize="2xl">
                    {formatNumber(reportData.funnelSteps?.[0]?.totalEvents || 0)}
                  </StatNumber>
                  <StatHelpText textAlign="center">
                    משתמשים שהתחילו הרשמה
                  </StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>השלמות הרשמה</Text>
                      <Icon as={FiTarget} ml={3} color="green.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="green.500" textAlign="center" fontSize="2xl">
                    {formatNumber(reportData.funnelSteps?.[(reportData.funnelSteps?.length ?? 1) - 1]?.totalEvents || 0)}
                  </StatNumber>
                  <StatHelpText textAlign="center">
                    משתמשים שהשלימו הרשמה
                  </StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>שיעור המרה כולל</Text>
                      <Icon as={FiTrendingUp} ml={3} color="purple.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="purple.500" textAlign="center" fontSize="2xl">
                    {formatPercentage(reportData.overallConversionRate || 0)}
                  </StatNumber>
                  <StatHelpText textAlign="center">
                    מהתחלה ועד סיום
                  </StatHelpText>
                </Stat>

                <Stat p={6} border="1px" borderColor={borderColor} borderRadius="lg" bg="white" boxShadow="sm">
                  <StatLabel>
                    <Flex align="center" justify="center">
                      <Text>נשירה כוללת</Text>
                      <Icon as={FiTrendingDown} ml={3} color="red.500" />
                    </Flex>
                  </StatLabel>
                  <StatNumber color="red.500" textAlign="center" fontSize="2xl">
                    {formatPercentage(100 - (reportData.overallConversionRate || 0))}
                  </StatNumber>
                  <StatHelpText textAlign="center">
                    משתמשים שלא השלימו
                  </StatHelpText>
                </Stat>
              </SimpleGrid>

              <Divider />

              {/* Tabs for different views */}
              <Tabs variant="enclosed" colorScheme="blue">
                <TabList justifyContent="center">
                  <Tab>ויזואליזציה</Tab>
                  <Tab>פירוט שלבים</Tab>
                  <Tab>ניתוח פרמטרים</Tab>
                </TabList>

                <TabPanels>
                  {/* Funnel Visualization Tab */}
                  <TabPanel>
                    <Box p={4} border="1px" borderColor={borderColor} borderRadius="lg" bg={chartBgColor}>
                      <VStack spacing={4}>
                        <Text fontSize="xl" fontWeight="bold" textAlign="center" color={chartTextColor}>
                          Registration Funnel
                        </Text>
                        <Text fontSize="sm" color="gray.500" textAlign="center">
                          ויזואליזציה של תהליך ההרשמה מהתחלה ועד סיום
                        </Text>
                        <CustomFunnelChart
                          data={prepareFunnelData(reportData.funnelSteps)}
                          onStepClick={(step) => {
                            console.log('Clicked step:', step);
                          }}
                        />
                      </VStack>
                    </Box>
                  </TabPanel>

                  {/* Step Details Tab */}
                  <TabPanel>
                    <VStack spacing={4} align="stretch">
                      {(reportData.funnelSteps || []).map((step, index) => (
                        <Box
                          key={step.stepNumber}
                          p={6}
                          border="1px"
                          borderColor={borderColor}
                          borderRadius="lg"
                          bg="white"
                          boxShadow="sm"
                        >
                          <HStack justify="space-between" align="center" mb={4}>
                            <VStack align="start" spacing={1}>
                              <Text fontSize="lg" fontWeight="bold" color="gray.800">
                                {step.stepName}
                              </Text>
                              <Text fontSize="sm" color="gray.500">
                                {step.eventName}
                              </Text>
                            </VStack>
                            <Badge colorScheme={getStepColor(index).replace('#', '')} fontSize="md" px={3} py={1}>
                              שלב {step.stepNumber}
                            </Badge>
                          </HStack>

                          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
                            <Stat textAlign="center">
                              <StatLabel>סה"כ אירועים</StatLabel>
                              <StatNumber color="blue.500" fontSize="2xl">
                                {formatNumber(step.totalEvents)}
                              </StatNumber>
                            </Stat>
                            <Stat textAlign="center">
                              <StatLabel>שיעור המרה</StatLabel>
                              <StatNumber color="green.500" fontSize="2xl">
                                {formatPercentage(step.conversionRate)}
                              </StatNumber>
                              <StatHelpText>מהשלב הקודם</StatHelpText>
                            </Stat>
                            <Stat textAlign="center">
                              <StatLabel>שיעור נשירה</StatLabel>
                              <StatNumber color="red.500" fontSize="2xl">
                                {formatPercentage(step.dropOffRate)}
                              </StatNumber>
                              <StatHelpText>מהשלב הקודם</StatHelpText>
                            </Stat>
                          </SimpleGrid>
                        </Box>
                      ))}
                    </VStack>
                  </TabPanel>

                  {/* Parameter Analysis Tab */}
                  <TabPanel>
                    <VStack spacing={6} align="stretch">
                      {(reportData.funnelSteps || []).map((step, stepIndex) => {
                        const hasParameters = step.parameterBreakdown && Object.keys(step.parameterBreakdown).length > 0;

                        if (!hasParameters) return null;

                        return (
                          <Box
                            key={step.stepNumber}
                            p={6}
                            border="1px"
                            borderColor={borderColor}
                            borderRadius="lg"
                            bg="white"
                            boxShadow="sm"
                          >
                            <Text fontSize="lg" fontWeight="bold" mb={4} textAlign="center">
                              {step.stepName} - ניתוח פרמטרים
                            </Text>

                            {Object.entries(step.parameterBreakdown).map(([paramName, paramValues]) => {
                              const chartData = Object.entries(paramValues).map(([value, count], index) => ({
                                name: value || 'לא מוגדר',
                                value: count,
                                fill: getStepColor(index),
                                percentage: ((count / step.totalEvents) * 100).toFixed(1)
                              }));

                              return (
                                <Box key={paramName} mb={8}>
                                  <Text fontSize="md" fontWeight="semibold" mb={4} textAlign="center">
                                    {paramName}
                                  </Text>

                                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8} w="100%">
                                    {/* Pie Chart */}
                                    <Box bg="white" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
                                      <Text fontSize="md" fontWeight="semibold" textAlign="center" mb={4} color="gray.700">
                                        התפלגות באחוזים
                                      </Text>
                                      <Box height="250px">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <PieChart>
                                            <Pie
                                              data={chartData}
                                              cx="50%"
                                              cy="50%"
                                              outerRadius={80}
                                              dataKey="value"
                                              label={({ name, percentage }) => `${name}: ${percentage}%`}
                                              labelLine={false}
                                              fontSize={12}
                                            >
                                              {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                              ))}
                                            </Pie>
                                            <Tooltip content={<CustomParameterTooltip />} />
                                          </PieChart>
                                        </ResponsiveContainer>
                                      </Box>
                                    </Box>

                                    {/* Bar Chart */}
                                    <Box bg="white" p={4} borderRadius="lg" border="1px" borderColor="gray.200">
                                      <Text fontSize="md" fontWeight="semibold" textAlign="center" mb={4} color="gray.700">
                                        כמויות מוחלטות
                                      </Text>
                                      <Box height="250px">
                                        <ResponsiveContainer width="100%" height="100%">
                                          <BarChart data={chartData} margin={{ top: 20, right: 30, left: 20, bottom: 80 }}>
                                            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                                            <XAxis
                                              dataKey="name"
                                              angle={-45}
                                              textAnchor="end"
                                              height={100}
                                              fontSize={11}
                                              fontWeight="500"
                                            />
                                            <YAxis fontSize={11} />
                                            <Tooltip content={<CustomParameterTooltip />} />
                                            <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                                              {chartData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.fill} />
                                              ))}
                                            </Bar>
                                          </BarChart>
                                        </ResponsiveContainer>
                                      </Box>
                                    </Box>
                                  </SimpleGrid>
                                </Box>
                              );
                            })}
                          </Box>
                        );
                      })}
                    </VStack>
                  </TabPanel>
                </TabPanels>
              </Tabs>

              {/* Summary */}
              {reportData.summary && (
                <Box p={4} border="1px" borderColor="blue.200" borderRadius="lg" bg="blue.50">
                  <Text fontSize="sm" color="blue.800" textAlign="center" whiteSpace="pre-line">
                    {reportData.summary}
                  </Text>
                </Box>
              )}
            </VStack>
          ) : (
            <Box textAlign="center" py={8}>
              <Text color="gray.500">לא נמצאו נתונים לתקופה שנבחרה</Text>
            </Box>
          )}
        </ModalBody>
        <ModalFooter borderTop="1px" borderColor="gray.200" justifyContent="center">
          <Button
            onClick={onClose}
            colorScheme="gray"
            size="lg"
            px={8}
            fontWeight="semibold"
          >
            סגור
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default RegistrationFunnelModal;
